/**
 * The membership-first site: the redesign that leads with Ahead membership
 * (the v4 homepage and pricing, a membership-first booking app, no separate
 * membership page), built across many PRs and switched on in one go.
 *
 * Two independent switches, both build-time:
 *   - WHEN: `RELEASE_MEMBERSHIP_FIRST` (`membershipFirstSiteEnabled`), set per
 *     Vercel environment. Preview has it on, Production has it off until
 *     launch, so merging to main changes nothing on the live site.
 *   - WHERE: `feature:membership-first-site` in `lib/country-gates.ts`. CH
 *     only today; another market joins by adding its code there.
 *
 * Code asks one question, `isMembershipFirstSite(country)`, and never reads
 * the env var or the gate on its own.
 */

import { isKnownCountryCode } from "@/i18n/config"
import { isAllowedInCountry } from "@/lib/country-gates"
import { membershipFirstSiteEnabled } from "@/lib/release-flags"

export function isMembershipFirstSite(country: string): boolean {
  return (
    membershipFirstSiteEnabled() &&
    isKnownCountryCode(country) &&
    isAllowedInCountry("feature:membership-first-site", country)
  )
}

/**
 * Which booking app a link opens: `membership-first` is the new app
 * (ahead-booking-new `apps/membership-first`, to be start.aheadhealth.com),
 * `legacy` is booking.aheadhealth.com. `lib/booking.ts` builds the links.
 */
export type BookingFlow = "membership-first" | "legacy"

/**
 * The flow a booking link in `country` opens. On the membership-first site
 * every page books into the new app, the ad pages under `/go` and `/lp`
 * included since 15.10 (Eugene); a one-off CTA that keeps the current app
 * passes `legacy` itself.
 */
export function defaultBookingFlow(country: string): BookingFlow {
  return isMembershipFirstSite(country) ? "membership-first" : "legacy"
}

/**
 * Pages the membership-first site no longer has. Their URLs forward to the
 * homepage, which now makes the membership case, and they leave the sitemap.
 */
const DROPPED_PAGES: readonly string[] = ["/membership"]

/** Whether `page` (a bare path such as `/membership`) is gone in `country`. */
export function isDroppedByMembershipFirstSite(
  page: string,
  country: string
): boolean {
  return DROPPED_PAGES.includes(page) && isMembershipFirstSite(country)
}

/**
 * Whether `pathname`, as `/{country}/{locale}/...`, is one of the blood ad
 * pages, `/go/bloodtest` or a city page such as `/go/bloodtest-zurich`. On the
 * membership-first site they render the Advanced blood test page with its
 * plan block, per Eugene on 07.10, so they book like it.
 */
export function isBloodGoPage(pathname: string): boolean {
  const [, , section, page] = pathname.split("/").filter(Boolean)
  return (
    section === "go" &&
    page !== undefined &&
    (page === "bloodtest" || page.startsWith("bloodtest-"))
  )
}

/**
 * Whether `pathname` is one of the full-body MRI ad pages, `/go/fullbodymri`
 * or a city page such as `/go/fullbodymri-zurich`. On the membership-first
 * site they render the Full-body MRI page as a landing, per Eugene on 08.10
 * and, for the cities, 09.10, so they book like it.
 */
export function isMriGoPage(pathname: string): boolean {
  const [, , section, page] = pathname.split("/").filter(Boolean)
  return (
    section === "go" &&
    page !== undefined &&
    (page === "fullbodymri" || page.startsWith("fullbodymri-"))
  )
}

/** Whether `pathname` is an ad page that renders a test page on the
 *  membership-first site: the blood ones and the full-body MRI one. */
export function isTestGoPage(pathname: string): boolean {
  return isBloodGoPage(pathname) || isMriGoPage(pathname)
}
